import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { useToast } from '../hooks/useToast'

interface SystemMetrics {
  system: {
    health: 'healthy' | 'degraded' | 'critical'
    uptime: number
    hostname?: string
    memory: {
      heapUsed: number
      heapTotal: number
      external: number
      rss: number
    }
    nodeVersion: string
    platform: string
  }
  cache: {
    connected: boolean
    memory: {
      used: number
      peak: number
    }
    keys: number
    hitRate: number
  }
  users: {
    total: number
    active: number
    inactive: number
    recentLogins: number
  }
  organizations: {
    total: number
    staff: number
    client: number
    recentGrowth: number
  }
  tickets: {
    total: number
    open: number
    closed: number
    resolutionRate: number
  }
  permissions: {
    roles: number
    permissions: number
  }
  performance: {
    responseTime: number
    timestamp: string
  }
}

const PlatformDashboard: React.FC = () => {
  const { showError } = useToast()
  const { showSuccess } = useToast()
  const [metrics, setMetrics] = useState<SystemMetrics | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [hasPendingMigrations, setHasPendingMigrations] = useState(false)
  const [devModeOperations, setDevModeOperations] = useState({
    clearingCache: false,
    reseedingPermissions: false,
    togglingCache: false,
    triggeringAutoClose: false
  })
  const [cacheEnabled, setCacheEnabled] = useState(true)
  const [imapStatus, setImapStatus] = useState<{
    isRunning: boolean
    isPolling?: boolean
    connected: boolean
    pollInterval?: number
    mailbox?: string
    lastPollAt?: string | null
    lastError?: string | null
    message?: string
    inboundEmailAddress?: string
    imapHost?: string
  } | null>(null)
  const [isPollingNow, setIsPollingNow] = useState(false)

  const fetchMetrics = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get('/api/platform/metrics', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })
      
      if (response.data.success) {
        setMetrics(response.data.data)
        setLastUpdated(new Date())
      }
    } catch (error: any) {
      console.error('Error fetching platform metrics:', error)
      showError('Failed to load platform metrics')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchImapStatus = async () => {
    try {
      const response = await axios.get('/api/imap-service/status', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })
      
      if (response.data.success) {
        setImapStatus(response.data.data)
      }
    } catch (error: any) {
      console.error('Error fetching IMAP status:', error)
      // Don't show error, just set status as not running
      setImapStatus({
        isRunning: false,
        connected: false,
        message: 'Not configured or unavailable'
      })
    }
  }

  const pollImapNow = async () => {
    try {
      setIsPollingNow(true)
      const response = await axios.post(
        '/api/imap-service/poll-once',
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      )

      if (response.data?.success) {
        const found = response.data?.data?.found ?? 0
        const forwarded = response.data?.data?.forwarded ?? 0
        const failed = response.data?.data?.failed ?? 0
        showSuccess(`Poll complete: ${found} found, ${forwarded} forwarded, ${failed} failed`)
        await fetchImapStatus()
      } else {
        showError('Failed to poll IMAP mailbox')
      }
    } catch (error: any) {
      showError(error.response?.data?.error || error.message || 'Failed to poll IMAP mailbox')
      console.error('Error polling IMAP mailbox:', error)
    } finally {
      setIsPollingNow(false)
    }
  }

  const checkPendingMigrations = async () => {
    try {
      const response = await axios.get('/api/setup/status')
      if (response.data.success && response.data.hasPendingMigrations) {
        setHasPendingMigrations(true)
      }
    } catch (error) {
      console.error('Error checking pending migrations:', error)
    }
  }

  const clearCache = async () => {
    try {
      setDevModeOperations(prev => ({ ...prev, clearingCache: true }))
      const response = await axios.post('/api/platform/clear-cache')
      if (response.data.success) {
        showSuccess('Cache cleared successfully!')
        fetchMetrics()
      }
    } catch (error) {
      showError('Failed to clear cache')
      console.error('Error clearing cache:', error)
    } finally {
      setDevModeOperations(prev => ({ ...prev, clearingCache: false }))
    }
  }

  const reseedPermissions = async () => {
    try {
      setDevModeOperations(prev => ({ ...prev, reseedingPermissions: true }))
      
      // Step 1: Run migrations first
      console.log('Running migrations...')
      const migrationResponse = await axios.post('/api/setup/run-migrations')
      if (!migrationResponse.data.success) {
        throw new Error('Migrations failed: ' + migrationResponse.data.details)
      }
      
      // Step 2: Then run seed
      console.log('Running seed...')
      const seedResponse = await axios.post('/api/setup/run-seed')
      if (!seedResponse.data.success) {
        throw new Error('Seeding failed: ' + seedResponse.data.details)
      }
      
      showSuccess('Database migrations and seed completed successfully!')
      
      // Refresh the page to show updated data
      setTimeout(() => {
        window.location.reload()
      }, 1000)
    } catch (error: any) {
      showError(error.response?.data?.details || error.message || 'Failed to run migrations and seed')
      console.error('Error running migrations and seed:', error)
    } finally {
      setDevModeOperations(prev => ({ ...prev, reseedingPermissions: false }))
    }
  }

  const fetchCacheStatus = async () => {
    try {
      const response = await axios.get('/api/platform/cache-status')
      if (response.data.success) {
        setCacheEnabled(response.data.data.enabled)
      }
    } catch (error) {
      console.error('Error fetching cache status:', error)
    }
  }

  const toggleCache = async () => {
    try {
      setDevModeOperations(prev => ({ ...prev, togglingCache: true }))
      const endpoint = cacheEnabled ? '/api/platform/disable-cache' : '/api/platform/enable-cache'
      const response = await axios.post(endpoint)
      
      if (response.data.success) {
        setCacheEnabled(!cacheEnabled)
        showSuccess(`Cache ${cacheEnabled ? 'disabled' : 'enabled'} successfully!`)
      }
    } catch (error) {
      showError('Failed to toggle cache')
      console.error('Error toggling cache:', error)
    } finally {
      setDevModeOperations(prev => ({ ...prev, togglingCache: false }))
    }
  }

  const triggerAutoClose = async () => {
    try {
      setDevModeOperations(prev => ({ ...prev, triggeringAutoClose: true }))
      const response = await axios.post('/api/platform/trigger-auto-close')
      
      if (response.data.success) {
        const closedCount = response.data.data?.closedCount || 0
        showSuccess(response.data.message || `Auto-close check completed. ${closedCount} ticket(s) closed.`)
        // Optionally refresh metrics to show updated ticket counts
        setTimeout(() => {
          fetchMetrics()
        }, 1000)
      }
    } catch (error: any) {
      showError(error.response?.data?.error || 'Failed to trigger auto-close')
      console.error('Error triggering auto-close:', error)
    } finally {
      setDevModeOperations(prev => ({ ...prev, triggeringAutoClose: false }))
    }
  }

  useEffect(() => {
    fetchMetrics()
    checkPendingMigrations()
    fetchCacheStatus()
    fetchImapStatus()
    
    // Refresh metrics every 30 seconds
    const interval = setInterval(() => {
      fetchMetrics()
      checkPendingMigrations()
      fetchCacheStatus()
      fetchImapStatus()
    }, 30000)
    return () => clearInterval(interval)
  }, [])

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400)
    const hours = Math.floor((seconds % 86400) / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    return `${days}d ${hours}h ${minutes}m`
  }

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const getHealthColor = (health: string) => {
    switch (health) {
      case 'healthy': return 'text-green-600 bg-green-100'
      case 'degraded': return 'text-yellow-600 bg-yellow-100'
      case 'critical': return 'text-red-600 bg-red-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  if (isLoading && !metrics) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!metrics) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Platform Metrics Unavailable</h2>
        <p className="text-gray-600 mb-4">Unable to load platform metrics</p>
        <button
          onClick={fetchMetrics}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="flex gap-4">
      {/* Main Content */}
      <div className="flex-1 space-y-4">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Platform Dashboard</h1>
            <p className="text-sm text-gray-600 mt-1">System metrics and platform health monitoring</p>
          </div>
          <div className="text-right">
            <div className="text-xs text-gray-500">
              Last updated: {lastUpdated?.toLocaleTimeString()}
            </div>
            <button
              onClick={fetchMetrics}
              className="mt-1 px-3 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Refresh
            </button>
          </div>
        </div>

      {/* Database Update Required Banner */}
      {hasPendingMigrations && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-yellow-800">
                Database Update Required
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>Your database schema needs to be updated. Click below to run the migration.</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => window.location.href = '/setup?mode=migration'}
                  className="bg-yellow-100 px-4 py-2 rounded-md text-sm font-medium text-yellow-800 hover:bg-yellow-200 mr-2"
                >
                  Update Database
                </button>
                <button
                  onClick={async () => {
                    try {
                      const response = await axios.post('/api/setup/run-seed')
                      if (response.data.success) {
                        alert('Permissions have been updated successfully!')
                        setHasPendingMigrations(false)
                      }
                    } catch (error) {
                      alert('Failed to update permissions: ' + (error as any).message)
                    }
                  }}
                  className="bg-blue-100 px-4 py-2 rounded-md text-sm font-medium text-blue-800 hover:bg-blue-200"
                >
                  Re-seed Permissions
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* System Health Overview */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <div className={`p-2 rounded-full ${getHealthColor(metrics.system.health)}`}>
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-500">System Health</p>
              <p className={`text-xl font-bold capitalize ${getHealthColor(metrics.system.health).split(' ')[0]}`}>
                {metrics.system.health}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 rounded-full bg-blue-100 text-blue-600">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-500">Uptime</p>
              <p className="text-xl font-bold text-gray-900">
                {formatUptime(metrics.system.uptime)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 rounded-full bg-green-100 text-green-600">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-500">Memory Usage</p>
              <p className="text-xl font-bold text-gray-900">
                {formatBytes(metrics.system.memory.heapUsed * 1024 * 1024)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 rounded-full bg-purple-100 text-purple-600">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-500">Cache Status</p>
              <p className={`text-xl font-bold ${metrics.cache.connected ? 'text-green-600' : 'text-red-600'}`}>
                {metrics.cache.connected ? 'Connected' : 'Disconnected'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <div className={`p-2 rounded-full ${imapStatus?.isRunning && imapStatus?.connected ? 'bg-green-100 text-green-600' : imapStatus?.isRunning ? 'bg-yellow-100 text-yellow-600' : 'bg-gray-100 text-gray-600'}`}>
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
              </svg>
            </div>
            <div className="ml-3">
              <div className="flex items-center gap-2">
                <p className="text-xs font-medium text-gray-500">Email Piping</p>
                <button
                  onClick={pollImapNow}
                  disabled={isPollingNow || !imapStatus?.isRunning}
                  className={`text-xs px-2 py-1 rounded border ${
                    isPollingNow || !imapStatus?.isRunning
                      ? 'text-gray-400 border-gray-200 cursor-not-allowed'
                      : 'text-blue-600 border-blue-200 hover:bg-blue-50'
                  }`}
                  title={!imapStatus?.isRunning ? 'IMAP service is not running' : 'Trigger a one-off poll now'}
                >
                  {isPollingNow ? 'Polling…' : 'Poll now'}
                </button>
              </div>
              <p className={`text-xl font-bold ${imapStatus?.isRunning && imapStatus?.connected ? 'text-green-600' : imapStatus?.isRunning ? 'text-yellow-600' : 'text-gray-600'}`}>
                {imapStatus?.isRunning && imapStatus?.connected ? 'Active' : imapStatus?.isRunning ? 'Connecting' : 'Inactive'}
              </p>
              {imapStatus?.pollInterval != null && (
                <p className="text-xs text-gray-500">
                  Polls every {Math.round(imapStatus.pollInterval / 1000)}s
                  {imapStatus?.lastPollAt ? ` • Last: ${new Date(imapStatus.lastPollAt).toLocaleString()}` : ''}
                </p>
              )}
              {(imapStatus?.inboundEmailAddress || imapStatus?.mailbox) && (
                <p className="text-xs text-gray-500">
                  Inbound: {imapStatus?.inboundEmailAddress ? imapStatus.inboundEmailAddress : '(not set)'}
                  {imapStatus?.mailbox ? ` • Mailbox: ${imapStatus.mailbox}` : ''}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* System Information */}
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-base font-semibold text-gray-900 mb-3">System Information</h3>
          <div className="space-y-3">
            {metrics.system.hostname && (
              <div className="flex justify-between">
                <span className="text-gray-600">Container Hostname:</span>
                <span className="font-medium text-blue-600 font-mono">{metrics.system.hostname}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-600">Node Version:</span>
              <span className="font-medium">{metrics.system.nodeVersion}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Platform:</span>
              <span className="font-medium">{metrics.system.platform}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Heap Used:</span>
              <span className="font-medium">{formatBytes(metrics.system.memory.heapUsed * 1024 * 1024)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Heap Total:</span>
              <span className="font-medium">{formatBytes(metrics.system.memory.heapTotal * 1024 * 1024)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">RSS Memory:</span>
              <span className="font-medium">{formatBytes(metrics.system.memory.rss * 1024 * 1024)}</span>
            </div>
          </div>
        </div>

        {/* Cache Information */}
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-base font-semibold text-gray-900 mb-3">Cache Information</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Status:</span>
              <span className={`font-medium ${metrics.cache.connected ? 'text-green-600' : 'text-red-600'}`}>
                {metrics.cache.connected ? 'Connected' : 'Disconnected'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Keys:</span>
              <span className="font-medium">{metrics.cache.keys.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Memory Used:</span>
              <span className="font-medium">{formatBytes(metrics.cache.memory.used)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Memory Peak:</span>
              <span className="font-medium">{formatBytes(metrics.cache.memory.peak)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Hit Rate:</span>
              <span className="font-medium">{metrics.cache.hitRate.toFixed(1)}%</span>
            </div>
          </div>
        </div>

        {/* User Statistics */}
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-base font-semibold text-gray-900 mb-3">User Statistics</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Total Users:</span>
              <span className="font-medium">{metrics.users.total.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Active Users:</span>
              <span className="font-medium text-green-600">{metrics.users.active.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Inactive Users:</span>
              <span className="font-medium text-gray-500">{metrics.users.inactive.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Recent Logins (24h):</span>
              <span className="font-medium text-blue-600">{metrics.users.recentLogins.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Organization Statistics */}
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-base font-semibold text-gray-900 mb-3">Organization Statistics</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Total Organizations:</span>
              <span className="font-medium">{metrics.organizations.total.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Staff Organizations:</span>
              <span className="font-medium text-blue-600">{metrics.organizations.staff.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Client Organizations:</span>
              <span className="font-medium text-green-600">{metrics.organizations.client.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Recent Growth (30d):</span>
              <span className="font-medium text-purple-600">{metrics.organizations.recentGrowth.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-base font-semibold text-gray-900 mb-3">Ticket Statistics</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Total Tickets:</span>
              <span className="font-medium">{metrics.tickets.total.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Open Tickets:</span>
              <span className="font-medium text-orange-600">{metrics.tickets.open.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Closed Tickets:</span>
              <span className="font-medium text-green-600">{metrics.tickets.closed.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Resolution Rate:</span>
              <span className="font-medium text-blue-600">{metrics.tickets.resolutionRate}%</span>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-base font-semibold text-gray-900 mb-3">Permission System</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Total Roles:</span>
              <span className="font-medium">{metrics.permissions.roles.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Total Permissions:</span>
              <span className="font-medium">{metrics.permissions.permissions.toLocaleString()}</span>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-base font-semibold text-gray-900 mb-3">Performance</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Response Time:</span>
              <span className="font-medium">{metrics.performance.responseTime}ms</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Last Updated:</span>
              <span className="font-medium text-sm">{new Date(metrics.performance.timestamp).toLocaleTimeString()}</span>
            </div>
          </div>
        </div>

      </div>
    </div>

      {/* System Controls Sidebar - Fixed */}
      <div className="w-64 flex-shrink-0">
        <div className="bg-gray-50 border-l border-gray-200 p-3 sticky top-4">

          <div className="space-y-3">
            {/* Cache Controls */}
            <div className="bg-white rounded-lg shadow-sm p-3">
              <h3 className="font-semibold text-gray-900 mb-2 text-sm">Cache Controls</h3>
              <div className="space-y-2">
                <button
                  onClick={toggleCache}
                  disabled={devModeOperations.togglingCache}
                  className={`w-full px-3 py-1.5 rounded-md text-white text-xs disabled:opacity-50 disabled:cursor-not-allowed ${
                    cacheEnabled 
                      ? 'bg-yellow-600 hover:bg-yellow-700' 
                      : 'bg-gray-600 hover:bg-gray-700'
                  }`}
                >
                  {devModeOperations.togglingCache 
                    ? 'Processing...' 
                    : cacheEnabled 
                      ? 'Disable Cache' 
                      : 'Enable Cache'}
                </button>
                <button
                  onClick={clearCache}
                  disabled={devModeOperations.clearingCache || !cacheEnabled}
                  className="w-full px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                >
                  {devModeOperations.clearingCache ? 'Clearing...' : 'Clear All Cache'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1.5">
                Cache is {cacheEnabled ? 'enabled' : 'disabled'}
              </p>
            </div>

            {/* System Operations */}
            <div className="bg-white rounded-lg shadow-sm p-3">
              <h3 className="font-semibold text-gray-900 mb-2 text-sm">Database Seed</h3>
              <button
                onClick={reseedPermissions}
                disabled={devModeOperations.reseedingPermissions}
                className="w-full px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
              >
                {devModeOperations.reseedingPermissions 
                  ? 'Running Seed...' 
                  : 'Run Database Seed'}
              </button>
              <p className="text-xs text-gray-500 mt-1.5">
                Seeds/updates all system data including roles, permissions, categories, SLA configs, and workflow rules
              </p>
            </div>

            {/* Auto-Close Trigger */}
            <div className="bg-white rounded-lg shadow-sm p-3">
              <h3 className="font-semibold text-gray-900 mb-2 text-sm">Auto-Close Tickets</h3>
              <button
                onClick={triggerAutoClose}
                disabled={devModeOperations.triggeringAutoClose}
                className="w-full px-3 py-1.5 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
              >
                {devModeOperations.triggeringAutoClose 
                  ? 'Triggering...' 
                  : 'Trigger Auto-Close Check'}
              </button>
              <p className="text-xs text-gray-500 mt-1.5">
                Manually trigger the auto-close check for tickets in pending_closure with no activity
              </p>
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-lg shadow-sm p-3">
              <h3 className="font-semibold text-gray-900 mb-2 text-sm">Quick Stats</h3>
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className={metrics.cache.connected ? 'text-green-600' : 'text-red-600'}>{metrics.cache.connected ? 'OK' : 'Disconnected'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Keys:</span>
                  <span className="font-medium">{metrics.cache.keys}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Hit Rate:</span>
                  <span className="font-medium">{metrics.cache.hitRate.toFixed(1)}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Memory:</span>
                  <span className="font-medium">{formatBytes(metrics.cache.memory.used * 1024 * 1024)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PlatformDashboard
